import { PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { createContext } from 'lib/createContext'
import { AllCompaniesMembersResponse } from 'api/company'
import {
  useAllCompanyMembers,
  useUpdateCompanyGroupMemberCompanies,
} from 'queries/company'
import { useDebounce } from 'ahooks'
import { useAuthContext } from 'components/auth'
import { toast } from '@workwhile/ui'

const PAGE_SIZE = 10

type CompanyMembersProviderState = {
  state: {
    searchTerm: string
    pageIndex: number
    membersPage: AllCompaniesMembersResponse['members']
    totalPages: number
    isLoading: boolean
    isFetchingNextPage: boolean
    hasNextPage: boolean
    isInCompanyGroup: boolean
    selectedMemberId: number | undefined
    isRemovingMember: boolean
    removeMemberError: string | undefined
  }
  actions: {
    setSearchTerm: (searchTerm: string) => void
    nextPage: () => void
    previousPage: () => void
    refetch: () => void
    setSelectedMemberId: (memberId: number | undefined) => void
    removeMember: (memberId: number, companyId: number) => void
  }
}

export const [CompanyMembersContext, useCompanyMembersContext] =
  createContext<CompanyMembersProviderState>({
    name: 'CompanyMembersContext',
  })

export function CompanyMembersProvider({ children }: PropsWithChildren) {
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [pageIndex, setPageIndex] = useState<number>(0)
  const [selectedMemberId, setSelectedMemberId] = useState<number | undefined>(
    undefined
  )

  const { company } = useAuthContext()
  const isInCompanyGroup = !!company?.companyGroup?.companies?.length

  const debouncedSearchTerm = useDebounce(searchTerm, { wait: 300 })
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isRefetching,
  } = useAllCompanyMembers(debouncedSearchTerm, PAGE_SIZE)
  const {
    mutate: updateCompanyGroupMemberCompanies,
    isPending: isUpdatingCompanyGroupMemberCompanies,
    error: removeMemberError,
  } = useUpdateCompanyGroupMemberCompanies()

  useEffect(() => {
    // Reset page index when search term changes
    setPageIndex(0)
  }, [debouncedSearchTerm])

  const handlePreviousPage = () => {
    setPageIndex((prev) => (prev > 0 ? prev - 1 : 0))
  }

  const handleNextPage = () => {
    const loadedPages = data?.pages.length ?? 0
    const hasMoreLoadedPages = pageIndex < loadedPages - 1
    if (hasMoreLoadedPages || hasNextPage) {
      setPageIndex((prev) => prev + 1)

      if (!hasMoreLoadedPages && hasNextPage) {
        fetchNextPage()
      }
    }
  }

  const handleRemoveMember = (memberId: number, companyId: number) => {
    updateCompanyGroupMemberCompanies(
      {
        userId: memberId,
        addCompanyIds: [],
        removeCompanyIds: [companyId],
      },
      {
        onSuccess: () => {
          toast.success('Successfully removed company access.')
          refetch()
          setSelectedMemberId(undefined)
        },
      }
    )
  }

  const value = useMemo(() => {
    const membersPage = data?.pages[pageIndex]?.members ?? []
    const totalPages = data?.pages.length ?? 0

    return {
      state: {
        searchTerm,
        pageIndex,
        membersPage,
        totalPages,
        isLoading: isLoading || isRefetching,
        isFetchingNextPage,
        hasNextPage,
        isInCompanyGroup,
        selectedMemberId,
        isRemovingMember: isUpdatingCompanyGroupMemberCompanies,
        removeMemberError: removeMemberError?.message,
      },
      actions: {
        setSearchTerm,
        nextPage: handleNextPage,
        previousPage: handlePreviousPage,
        refetch,
        setSelectedMemberId,
        removeMember: handleRemoveMember,
      },
    } as CompanyMembersProviderState
  }, [
    searchTerm,
    pageIndex,
    data?.pages,
    isLoading,
    isRefetching,
    isFetchingNextPage,
    hasNextPage,
    refetch,
    isInCompanyGroup,
    selectedMemberId,
    removeMemberError,
  ])

  return (
    <CompanyMembersContext.Provider value={value}>
      {children}
    </CompanyMembersContext.Provider>
  )
}
